import AddCommentIcon from '@mui/icons-material/AddComment';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import LockIcon from '@mui/icons-material/Lock';
import SendIcon from '@mui/icons-material/Send';
import {
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ASSISTANT_SUGGESTIONS } from '@app/shared';
import type { AssistantAnswerDto } from '@app/shared';
import { tokens } from '../../app/theme';
import { useAskAssistant } from './api';

function Answer({ answer, onFollow }: { answer: AssistantAnswerDto; onFollow: () => void }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }} aria-label={`Answer: ${answer.title}`}>
      <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{answer.title}</Typography>
      {answer.facts.length ? (
        <Table size="small">
          <TableBody>
            {answer.facts.map((f) => (
              <TableRow key={f.label}>
                <TableCell component="th" scope="row" sx={{ pl: 0, color: tokens.cardTitle }}>
                  {f.label}
                </TableCell>
                <TableCell>{f.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
      {answer.note ? <Typography variant="body2">{answer.note}</Typography> : null}
      {answer.links.length ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
          {answer.links.map((l) => (
            <MuiLink key={l.to} component={Link} to={l.to} onClick={onFollow}>
              {l.label}
            </MuiLink>
          ))}
        </Box>
      ) : null}
    </Paper>
  );
}

/**
 * AI Assistant (release switch `ai-assistant`): the owner's read-only lookup, with no outside
 * AI. The chat and its history last while the user is signed in on this page.
 */
export function AssistantPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [chat, setChat] = useState<AssistantAnswerDto[]>([]);
  const [history, setHistory] = useState<string[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [text, setText] = useState('');
  const asking = useAskAssistant();

  const send = async (question: string) => {
    const q = question.trim();
    if (!q || asking.isPending) return;
    setShowHistory(false);
    try {
      const answer = await asking.mutateAsync(q);
      setChat((c) => [...c, answer]);
      setHistory((h) => [q, ...h.filter((x) => x !== q)].slice(0, 50));
      setText('');
    } catch {
      // The error is shown under the box.
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void send(text);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      keepMounted
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 420 } } } }}
    >
      <Box
        role="region"
        aria-label="AI Assistant"
        sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <Box sx={{ p: 2, borderBottom: `1px solid ${tokens.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
              AI Assistant
            </Typography>
            <IconButton aria-label="Close" size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Status: ready. Ask about customers, inventory, reports &amp; more
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button
              size="small"
              startIcon={<HistoryIcon />}
              aria-pressed={showHistory}
              onClick={() => setShowHistory((v) => !v)}
            >
              History
            </Button>
            <Button
              size="small"
              startIcon={<AddCommentIcon />}
              onClick={() => {
                setChat([]);
                setShowHistory(false);
                asking.reset();
              }}
            >
              New chat
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            p: 2,
            display: 'grid',
            gap: 1.5,
            alignContent: 'start',
          }}
        >
          {showHistory ? (
            <Box role="region" aria-label="Chat history">
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Chat history</Typography>
              {history.length ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  {history.map((q) => (
                    <Button key={q} size="small" onClick={() => void send(q)}>
                      {q}
                    </Button>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No history yet. Your questions will show up here after you chat.
                </Typography>
              )}
            </Box>
          ) : (
            chat.map((a, i) => (
              <Box key={i} sx={{ display: 'grid', gap: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    justifySelf: 'end',
                    bgcolor: tokens.pageBg,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                  }}
                >
                  {a.question}
                </Typography>
                <Answer answer={a} onFollow={onClose} />
              </Box>
            ))
          )}
        </Box>

        <Box sx={{ p: 2, borderTop: `1px solid ${tokens.border}` }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
            {ASSISTANT_SUGGESTIONS.map((s) => (
              <Chip key={s} label={s} size="small" onClick={() => void send(s)} />
            ))}
          </Box>
          <Box component="form" onSubmit={submit} sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Try: customer ID, enroll ID, SIM number, or a report name…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 500, 'aria-label': 'Ask the AI Assistant' } }}
            />
            <IconButton type="submit" color="primary" aria-label="Send" disabled={asking.isPending}>
              <SendIcon />
            </IconButton>
          </Box>
          {asking.isError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {asking.error instanceof Error
                ? asking.error.message
                : 'The question was not answered.'}
            </Alert>
          ) : null}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}
          >
            <LockIcon sx={{ fontSize: 14 }} /> Read-only &amp; secure
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
}
